import { countOrNull, finiteNumber, isRecord } from "./service-json";

/**
 * La salle qui parle : le fil de discussion d'un récital, et ses cœurs.
 *
 * # Ce que c'est, et ce que ce n'est pas
 *
 * Du direct, et rien d'autre. Rien n'est gardé — ni ici, ni au service, ni
 * dans l'enregistrement du récital (décision de l'utilisateur, 2026-09-23) :
 * qui entre voit ce qui se dit à partir de là, et une page rechargée repart
 * d'un fil vide. C'est ce qui rend ce module si court : un magasin de ce qui
 * est arrivé pendant qu'on était là, et un exutoire pour ce qu'on tape.
 *
 * # D'où ça vient, où ça va
 *
 * Tout arrive par le canal de données de la salle, **signé du service** : un
 * spectateur ne peut rien y publier, et c'est le service qui parle à sa place
 * sous le nom de son jeton (voir `session-control.ts`, `sendChatMessage`).
 * Tout part par HTTP vers ce même service, qui borne le débit de chacun et
 * additionne les cœurs avant de les rendre à la salle.
 *
 * # Un magasin hors de React
 *
 * Le contexte live est lourd, et tout le studio le lit : le repeindre à chaque
 * message — et quatre fois par seconde quand les cœurs pleuvent — coûterait
 * à tout l'écran. Le fil et les cœurs vivent donc ici, et seuls les widgets
 * qui les montrent s'y abonnent (`useSyncExternalStore`).
 *
 * # La modération
 *
 * Elle arrive par le même canal, sur son sujet (`virtuosa.hall`) : un message
 * à oublier, pour toute la salle ; une parole retirée ou rendue, pour la
 * personne et l'interprète seuls — le service choisit ses destinataires, et
 * la salle n'apprend jamais qui est en sourdine. Le chat fermé, lui, est un
 * fait de séance et vit dans l'état publié (`LiveSessionState.chatClosed`),
 * pas ici. Les gestes de l'interprète partent par les mêmes liens que la
 * parole ; le service les refuse à tout autre.
 */

/** Sujet des messages du fil, sur le canal de données. */
export const CHAT_TOPIC = "virtuosa.chat";

/** Sujet des comptes de cœurs. */
export const REACTIONS_TOPIC = "virtuosa.reactions";

/** Sujet de la modération : un message retiré, une parole retirée ou rendue. */
export const HALL_TOPIC = "virtuosa.hall";

/** Le fil ne garde que ce qui se lit encore : au-delà, le plus ancien part. */
export const HALL_KEEP = 200;

/** Qui a parlé, tel que son jeton le dit. */
export interface HallSpeaker {
  readonly identity: string;
  /** Jamais vide : le service prend le nom du jeton, et l'API y met un repli. */
  readonly name: string;
  /** L'interprète, dans un récital : le seul insigne qu'un message porte. */
  readonly canPlay: boolean;
}

/** Un message du fil, tel que le service l'a publié. */
export interface HallMessage {
  readonly id: string;
  /** L'instant du service, en millisecondes Unix. */
  readonly at: number;
  readonly from: HallSpeaker;
  readonly text: string;
}

/** Un compte de cœurs publié par le service : ce qui s'est ajouté, et le total. */
export interface HeartBurst {
  readonly hearts: number;
  readonly total: number;
}

/**
 * Lit un message du fil, ou rend `null`.
 *
 * Même tolérance que `parseSessionState` : on ne valide que ce dont on se
 * sert, et une clé inconnue est ignorée — un service plus récent peut en
 * ajouter. Un message sans identité, sans texte ou sans instant lisible ne
 * s'affiche pas : on ne saurait ni le signer ni le ranger.
 */
export function readHallMessage(value: unknown): HallMessage | null {
  if (!isRecord(value) || typeof value.id !== "string" || value.id === "") {
    return null;
  }

  if (typeof value.text !== "string" || value.text === "") {
    return null;
  }

  const at = finiteNumber(value.at_ms);
  const from = value.from;

  if (at === null || !isRecord(from) || typeof from.identity !== "string" || from.identity === "") {
    return null;
  }

  return {
    id: value.id,
    at,
    from: {
      identity: from.identity,
      name: typeof from.name === "string" && from.name !== "" ? from.name : "Invité",
      canPlay: from.can_play === true,
    },
    text: value.text,
  };
}

/**
 * Un geste de modération, tel que le service le publie : un message à oublier,
 * ou une parole retirée (`muted`) ou rendue à `who` — les onglets d'une même
 * personne, dont peut-être le nôtre.
 */
export interface HallNotice {
  readonly retract: string | null;
  readonly muted: boolean | null;
  readonly who: readonly string[];
}

/** Lit un geste de modération, ou rend `null` s'il ne dit rien. */
export function readHallNotice(value: unknown): HallNotice | null {
  if (!isRecord(value)) {
    return null;
  }

  const retract = typeof value.retract === "string" && value.retract !== "" ? value.retract : null;
  const muted = typeof value.muted === "boolean" ? value.muted : null;
  const who = Array.isArray(value.who) ? value.who.filter((id): id is string => typeof id === "string" && id !== "") : [];

  if (retract === null && muted === null) {
    return null;
  }

  return { retract, muted, who };
}

/** Lit un compte de cœurs, ou rend `null`. */
export function readHeartBurst(value: unknown): HeartBurst | null {
  if (!isRecord(value)) {
    return null;
  }

  const hearts = countOrNull(value.heart);
  const total = countOrNull(value.total);

  if (hearts === null) {
    return null;
  }

  return { hearts, total: total ?? hearts };
}

/**
 * Regroupe les cœurs qu'on tape, et reconnaît les siens quand ils reviennent.
 *
 * # Regrouper
 *
 * Un pouce qui martèle fait dix gestes par seconde. Le premier part tout de
 * suite — un cœur isolé ne doit pas attendre une fenêtre —, les suivants
 * s'additionnent pendant `intervalMs` et partent en un seul envoi. Trois
 * requêtes par seconde au plus, quel que soit le rythme du pouce.
 *
 * # Reconnaître les siens
 *
 * Le service additionne ce que toute la salle envoie et rend un compte à tous,
 * émetteur compris. Le cœur qu'on vient de taper est déjà à l'écran, tout de
 * suite, sans attendre le service ; le revoir arriver un quart de seconde plus
 * tard dans le compte de la salle ferait un écho. On retient donc ce qu'on a
 * envoyé, et on le retire du premier compte qui revient : sur la durée, la
 * somme des comptes est exactement la somme de ce que chacun a tapé, donc ce
 * qu'on retire est bien à nous — au pire pris sur la fenêtre d'à côté, ce qui
 * ne se voit pas.
 *
 * Ce qu'on a envoyé s'oublie au bout de `echoMs` s'il n'est pas revenu — un
 * compte perdu en route, un service qui a redémarré. Sans cette échéance, on
 * retiendrait pour toujours des cœurs qui ne sont pas les nôtres. Un envoi
 * refusé s'oublie tout de suite.
 */
export class ReactionOutbox {
  private count = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  /** Ce qui est parti et n'est pas encore revenu, avec l'échéance de chaque envoi. */
  private echoes: { count: number; until: number }[] = [];
  private disposed = false;

  constructor(
    private readonly send: (count: number) => Promise<boolean>,
    private readonly intervalMs = 300,
    private readonly echoMs = 2_000,
    private readonly now: () => number = () => Date.now(),
  ) {}

  /** Un cœur de plus. Part tout de suite, ou avec les suivants. */
  tap(): void {
    if (this.disposed) {
      return;
    }

    this.count += 1;

    if (this.timer === null) {
      this.flush();
    }
  }

  /**
   * Retire de `hearts` ce qui est à nous, et rend ce qui vient des autres —
   * ce qu'il faut faire tomber à l'écran.
   */
  absorb(hearts: number): number {
    const now = this.now();
    let rest = hearts;

    this.echoes = this.echoes.filter((echo) => echo.until > now);

    for (const echo of this.echoes) {
      const mine = Math.min(echo.count, rest);
      echo.count -= mine;
      rest -= mine;
    }

    this.echoes = this.echoes.filter((echo) => echo.count > 0);

    return rest;
  }

  /** Ce qu'on a tapé et que la salle n'a pas encore rendu : à ajouter au total qu'elle publie. */
  get inFlight(): number {
    const now = this.now();

    return this.count + this.echoes.reduce((sum, echo) => (echo.until > now ? sum + echo.count : sum), 0);
  }

  dispose(): void {
    this.disposed = true;

    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    this.count = 0;
    this.echoes = [];
  }

  private flush(): void {
    const count = this.count;
    this.count = 0;

    if (count === 0) {
      this.timer = null;

      return;
    }

    const echo = { count, until: this.now() + this.echoMs };
    this.echoes.push(echo);

    void this.send(count).then((sent) => {
      if (!sent) {
        echo.count = 0;
      }
    });

    this.timer = setTimeout(() => this.flush(), this.intervalMs);
  }
}

/** Ce qu'un envoi de message devient. Voir `sendChatMessage`. */
export type HallSendOutcome = "sent" | "too-fast" | "muted" | "closed" | "failed";

/** Par où la salle parle au service : injecté, parce que ce module ne connaît ni HTTP ni le billet. */
export interface HallLinks {
  say(text: string): Promise<HallSendOutcome>;
  react(count: number): Promise<boolean>;
  /** Les gestes de l'interprète. Le service les refuse à tout autre. */
  retract(id: string): Promise<boolean>;
  mute(identity: string, muted: boolean): Promise<boolean>;
  exclude(identity: string): Promise<boolean>;
}

type HeartsListener = (hearts: number, total: number) => void;

/**
 * Le fil et les cœurs d'une salle, pendant qu'on y est.
 *
 * Un magasin pour `useSyncExternalStore` : `subscribe` et `snapshot` pour le
 * fil, dont chaque message arrivé fait un nouvel instantané ; `onHearts` pour
 * les cœurs, qui ne sont pas un état mais un événement — on les fait tomber,
 * on ne les garde pas.
 */
export class LiveHall {
  private messages: readonly HallMessage[] = [];
  private readonly listeners = new Set<() => void>();
  private readonly heartsListeners = new Set<HeartsListener>();
  private readonly outbox: ReactionOutbox;
  private readonly decoder = new TextDecoder();
  /** Le dernier total publié par la salle. */
  private published = 0;
  /** Notre identité dans la salle : c'est elle qu'on cherche dans `who`. */
  private readonly identity: string;
  /** L'interprète nous a retiré la parole. */
  private _muted = false;
  /**
   * Ce que l'interprète a appris de ses propres gestes, par identité : vrai
   * en sourdine, faux rendue. Ce qui n'y est pas se lit sur le relevé des
   * participants ; ce qui y est le précède, le temps d'un relevé.
   */
  private mutedBy: ReadonlyMap<string, boolean> = new Map();
  /**
   * Les numéros retirés : un retrait qui devancerait son message — deux sujets,
   * un seul canal fiable, mais on ne le jure pas — ne doit pas laisser passer
   * la ligne ensuite.
   */
  private readonly retracted = new Set<string>();

  constructor(
    private readonly links: HallLinks,
    options: { identity?: string; intervalMs?: number; echoMs?: number; now?: () => number } = {},
  ) {
    this.identity = options.identity ?? "";
    this.outbox = new ReactionOutbox((count) => links.react(count), options.intervalMs, options.echoMs, options.now);
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Le fil, du plus ancien au plus récent. Un nouvel objet à chaque message. */
  snapshot(): readonly HallMessage[] {
    return this.messages;
  }

  /** L'interprète nous a retiré la parole. Même abonnement que le fil. */
  get muted(): boolean {
    return this._muted;
  }

  /** Ce que l'interprète sait de ses sourdines, par identité. Une nouvelle table à chaque geste appris. */
  get mutedIdentities(): ReadonlyMap<string, boolean> {
    return this.mutedBy;
  }

  /** Les cœurs des autres, à faire tomber, et le total de la salle. */
  onHearts(listener: HeartsListener): () => void {
    this.heartsListeners.add(listener);

    return () => {
      this.heartsListeners.delete(listener);
    };
  }

  /** Le total de la salle, nos cœurs en route compris. */
  get heartsTotal(): number {
    return this.published + this.outbox.inFlight;
  }

  /**
   * Un paquet du service, sur son sujet. Un sujet inconnu ou un paquet
   * illisible est ignoré : c'est la seule réaction qui n'aggrave pas.
   */
  receive(topic: string, payload: Uint8Array): void {
    if (topic !== CHAT_TOPIC && topic !== REACTIONS_TOPIC && topic !== HALL_TOPIC) {
      return;
    }

    let body: unknown;

    try {
      body = JSON.parse(this.decoder.decode(payload));
    } catch {
      return;
    }

    if (topic === CHAT_TOPIC) {
      this.append(readHallMessage(body));

      return;
    }

    if (topic === HALL_TOPIC) {
      this.moderate(readHallNotice(body));

      return;
    }

    const burst = readHeartBurst(body);

    if (burst === null) {
      return;
    }

    this.published = Math.max(this.published, burst.total);

    const others = this.outbox.absorb(burst.hearts);

    for (const listener of this.heartsListeners) {
      listener(others, this.heartsTotal);
    }
  }

  /**
   * Dit quelque chose. Le message reviendra par la salle, s'il est parti.
   *
   * Le refus « parole retirée » vaut ce que le paquet aurait dit : une page
   * rechargée ne l'a pas reçu, et c'est le premier envoi qui l'apprend. Un
   * message accepté, à l'inverse, prouve qu'on parle — quoi qu'on ait cru.
   */
  async say(text: string): Promise<HallSendOutcome> {
    const outcome = await this.links.say(text);

    if (outcome === "muted" && !this._muted) {
      this._muted = true;
      this.notify();
    } else if (outcome === "sent" && this._muted) {
      this._muted = false;
      this.notify();
    }

    return outcome;
  }

  /** Fait oublier un message à toute la salle. Le retrait revient par la salle, comme un message. */
  retract(id: string): Promise<boolean> {
    return this.links.retract(id);
  }

  /** Retire la parole à quelqu'un, ou la lui rend. Ce que ça a fait revient par la salle. */
  mute(identity: string, muted: boolean): Promise<boolean> {
    return this.links.mute(identity, muted);
  }

  /** Sort quelqu'un du récital, pour le reste de la séance. */
  exclude(identity: string): Promise<boolean> {
    return this.links.exclude(identity);
  }

  /** Un cœur. À montrer tout de suite chez soi : la salle le rendra sans le compter deux fois. */
  tap(): void {
    this.outbox.tap();
  }

  dispose(): void {
    this.outbox.dispose();
    this.listeners.clear();
    this.heartsListeners.clear();
  }

  private append(message: HallMessage | null): void {
    // Le canal est fiable et ordonné, mais une reconnexion peut rejouer ce qui
    // était en vol : le même numéro deux fois ne fait qu'une ligne. Un numéro
    // retiré n'en fait aucune.
    if (message === null || this.retracted.has(message.id) || this.messages.some((held) => held.id === message.id)) {
      return;
    }

    const next = [...this.messages, message];

    // Un nouvel objet à chaque message : `useSyncExternalStore` compare les
    // instantanés par identité.
    this.messages = next.length > HALL_KEEP ? next.slice(next.length - HALL_KEEP) : next;
    this.notify();
  }

  private moderate(notice: HallNotice | null): void {
    if (notice === null) {
      return;
    }

    let changed = false;

    if (notice.retract !== null) {
      this.retracted.add(notice.retract);

      const kept = this.messages.filter((held) => held.id !== notice.retract);

      if (kept.length !== this.messages.length) {
        this.messages = kept;
        changed = true;
      }
    }

    if (notice.muted !== null && notice.who.length > 0) {
      const next = new Map(this.mutedBy);

      for (const identity of notice.who) {
        next.set(identity, notice.muted);
      }

      this.mutedBy = next;
      changed = true;

      if (this.identity !== "" && notice.who.includes(this.identity)) {
        this._muted = notice.muted;
      }
    }

    if (changed) {
      this.notify();
    }
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }
}
